import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
interface Founder {
  name: string;
  titleKey: string;
  quoteKey: string;
  image: string;
  year: string;
}
const founders: Founder[] = [{
  name: 'Aliko Dangote',
  titleKey: 'carousel.dangote.title',
  quoteKey: 'carousel.dangote.quote',
  image: 'https://cdn.magicpatterns.com/uploads/xwzAQewZ2T1KZJEVqv6wdp/ChatGPT_Image_13_janv._2026,_22_01_28.png',
  year: '1977'
}, {
  name: 'Pierre Fakhoury',
  titleKey: 'carousel.fakhoury.title',
  quoteKey: 'carousel.fakhoury.quote',
  image: 'https://cdn.magicpatterns.com/uploads/x6hFKd9RQT55nr2cZnZgeu/ChatGPT_Image_13_janv._2026,_22_13_45.png',
  year: '1960'
}, {
  name: 'Strive Masiyiwa',
  titleKey: 'carousel.masiyiwa.title',
  quoteKey: 'carousel.masiyiwa.quote',
  image: 'https://cdn.magicpatterns.com/uploads/aMWXhV4eFAvgvwMjeWTWjE/ChatGPT_Image_13_janv._2026,_22_23_58.png',
  year: '1993'
}, {
  name: 'Nicky Oppenheimer',
  titleKey: 'carousel.oppenheimer.title',
  quoteKey: 'carousel.oppenheimer.quote',
  image: 'https://cdn.magicpatterns.com/uploads/8UPzzkRs1HKnfY3jEwoWhJ/ChatGPT_Image_13_janv._2026,_22_31_25.png',
  year: '1985'
}, {
  name: 'Mo Ibrahim',
  titleKey: 'carousel.ibrahim.title',
  quoteKey: 'carousel.ibrahim.quote',
  image: 'https://cdn.magicpatterns.com/uploads/cRtqUHNqAhdC2QYJ3DE9Rr/ChatGPT_Image_13_janv._2026,_22_38_56.png',
  year: '1998'
}, {
  name: 'Issad Rebrab',
  titleKey: 'carousel.rebrab.title',
  quoteKey: 'carousel.rebrab.quote',
  image: 'https://cdn.magicpatterns.com/uploads/oywGNuWNWYTaDTY7NtLgoy/ChatGPT_Image_13_janv._2026,_22_45_27.png',
  year: '1971'
}];
export function FoundersCarousel() {
  const {
    t
  } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const goToSlide = (index: number) => {
    if (index === currentIndex) return;
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };
  const goToNext = () => {
    const newIndex = currentIndex === founders.length - 1 ? 0 : currentIndex + 1;
    goToSlide(newIndex);
  };
  // Auto-advance every 8 seconds (pause on hover)
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      goToNext();
    }, 8000);
    return () => clearInterval(timer);
  }, [currentIndex, isPaused]);
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 100 : -100,
      opacity: 0
    })
  };
  const currentFounder = founders[currentIndex];
  return <section id="founders" className="relative py-12 md:py-16 bg-earth-sand overflow-hidden scroll-mt-20" data-id="element-127">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-pattern-kente pointer-events-none" data-id="element-128" />

      {/* Top Border */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gold via-earth-terracotta to-gold" data-id="element-129" />

      <div className="max-w-6xl mx-auto relative z-10 w-full px-4 md:px-8 lg:px-12" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} data-id="element-130">
        {/* Carousel Content */}
        <div className="relative min-h-[500px] flex items-center" data-id="element-131">
          <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-132">
            <motion.div key={currentIndex} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
            x: {
              type: 'spring',
              stiffness: 300,
              damping: 30
            },
            opacity: {
              duration: 0.3
            }
          }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center w-full" data-id="element-133">
              {/* Left: Portrait with Paper Aesthetic */}
              <div className="relative max-w-sm mx-auto lg:mx-0 w-full" data-id="element-134">
                {/* Just the photo - no frame */}
                <div className="aspect-[3/4] overflow-hidden relative shadow-xl rounded-lg" data-id="element-135">
                  <img src={currentFounder.image} alt={currentFounder.name} className="w-full h-full object-cover grayscale contrast-125 sepia-[.15]" data-id="element-136" />
                </div>
              </div>

              {/* Right: Quote and Details */}
              <div className="space-y-6" data-id="element-137">
                <div data-id="element-138">
                  <h3 className="text-3xl md:text-4xl font-serif font-bold text-charcoal-900 mb-2" data-id="element-139">
                    {currentFounder.name}
                  </h3>
                  <p className="font-sans text-earth-terracotta uppercase tracking-wider text-sm font-bold" data-id="element-140">
                    {t(currentFounder.titleKey)}
                  </p>
                </div>

                <div className="relative" data-id="element-141">
                  <Quote className="absolute -top-4 -left-2 w-12 h-12 text-gold opacity-30 fill-current" data-id="element-142" />
                  <blockquote className="font-serif text-xl md:text-2xl text-charcoal-700 leading-relaxed pl-8 border-l-4 border-gold italic" data-id="element-143">
                    "{t(currentFounder.quoteKey)}"
                  </blockquote>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Progress Indicators */}
        <div className="flex justify-center mt-12" data-id="element-144">
          <div className="flex space-x-3 bg-white/50 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20" data-id="element-145">
            {founders.map((_, idx) => <button key={idx} onClick={() => goToSlide(idx)} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${idx === currentIndex ? 'bg-gold scale-125' : 'bg-charcoal-300 hover:bg-charcoal-400'}`} aria-label={`Go to slide ${idx + 1}`} data-id="element-146" />)}
          </div>
        </div>
      </div>

      {/* Bottom Decorative Element */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-earth-terracotta via-gold to-earth-ochre" data-id="element-147" />
    </section>;
}